<script setup lang="ts">
const { isAuthenticated } = storeToRefs(useAuthStore());
const { currentRouteName } = useRoutes();

type Emits = {
  onRefresh: [];
};

const emit = defineEmits<Emits>();

const isLoading = ref<boolean>(false);
const isGpsPermissionsTutorialVisible = ref<boolean>(false);

function onGpsButtonSuccess() {
  isGpsPermissionsTutorialVisible.value = false;

  emit('onRefresh');
}

function onGpsButtonError() {
  isGpsPermissionsTutorialVisible.value = true;

  emit('onRefresh');

  if (!isAuthenticated.value && currentRouteName.value !== 'index') {
    navigateTo(localeLink('/pending'));
  }
}

function onLoadingChange(isLoadingState: boolean) {
  isLoading.value = isLoadingState;
}

function onBackButtonClick() {
  isLoading.value = false;
  isGpsPermissionsTutorialVisible.value = false;
}
</script>

<template>
  <div :class="$style.boxContainer">
    <BaseLoading
      class="!bg-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      v-if="isGpsPermissionsTutorialVisible"
      :class="$style.tutorialContent"
    >
      <AuthVerificationGpsTutorial />

      <div :class="$style.tutorialButtons">
        <button
          :class="$style.backButton"
          @click="onBackButtonClick"
        >
          <NuxtIcon name="arrows/arrow_left" />
          {{ $t('general.back') }}
        </button>

        <AuthVerificationGpsButton
          :button-label="$t('general.try_again')"
          @on-error="onGpsButtonError"
          @on-loading="onLoadingChange"
          @on-success="onGpsButtonSuccess"
        />
      </div>
    </div>

    <section v-else>
      <img
        alt="Gps icon"
        aria-hidden="true"
        :class="$style.modalIcon"
        :src="getAssetsUrlForCDN('/assets/images/svg/gps.svg')"
      />

      <p :class="$style.title">
        {{ $t('verification.gps.title') }}
      </p>

      <p
        :class="$style.description"
        v-html="$t('verification.gps.desc')"
      />

      <div :class="$style.buttonContainer">
        <AuthVerificationGpsButton
          :hide-button="isGpsPermissionsTutorialVisible"
          @on-error="onGpsButtonError"
          @on-loading="onLoadingChange"
          @on-success="onGpsButtonSuccess"
        />
      </div>

      <div :class="$style.alertGeoLocationPermission">
        <p v-html="$t('verification.gps.alert')" />
      </div>
    </section>
  </div>
</template>

<style module lang="scss">
.boxContainer {
  @apply p-3 md:p-6 flex flex-col justify-center flex-1 text-texts text-center;

  .modalIcon {
    @apply w-28 h-28 mx-auto mb-5;
  }

  .title {
    @apply font-bold mb-3;
  }

  .description {
    @apply mb-1 max-w-56 inline-block mx-auto text-sm text-texts/80;

    strong {
      @apply text-primary/80;
    }
  }

  .tutorialContent {
    @apply text-sm;

    .tutorialButtons {
      @apply flex items-stretch justify-center gap-2 mt-6;

      .backButton {
        @apply justify-center text-xs rounded-md gap-1.5 flex items-center font-bold uppercase py-2.5 focus:outline-none focus:ring-4 bg-texts/10 text-texts/50 px-3;
      }
    }
  }

  .buttonContainer {
    @apply mt-6 flex items-center justify-center;

    .submitBtn {
      @apply mx-auto rounded-md gap-1.5 flex items-center font-bold uppercase py-2.5 focus:outline-none focus:ring-4;
    }
  }

  .alertGeoLocationPermission {
    @apply text-warning text-2xs mt-6 max-w-[300px] mx-auto;
  }
}
</style>
